import type { ActionId, StaffFeatureId, UserFeatureId } from "@wallyo/contracts/actions";
import type { GetStaffMeResponse } from "@wallyo/contracts/console";
import type { DefinedPermissionId } from "@wallyo/contracts/permissions";
import type { DefinedRuleId, RuleCategory } from "@wallyo/contracts/rules";
import type { SettingCategory } from "@wallyo/contracts/settings";
import { ar } from "./ar";
import { de } from "./de";
import { en } from "./en";
import { es } from "./es";
import type { FeatureNames, OwnSettingId, RuleNames } from "./feature-names";
import { featuresAr } from "./features.ar";
import { featuresDe } from "./features.de";
import { featuresEn } from "./features.en";
import { featuresEs } from "./features.es";
import { featuresFr } from "./features.fr";
import { featuresIt } from "./features.it";
import { featuresNl } from "./features.nl";
import { featuresPt } from "./features.pt";
import { fr, type MessageId } from "./fr";
import { it } from "./it";
import { nl } from "./nl";
import { pt } from "./pt";
import { rulesAr } from "./rules.ar";
import { rulesDe } from "./rules.de";
import { rulesEn } from "./rules.en";
import { rulesEs } from "./rules.es";
import { rulesFr } from "./rules.fr";
import { rulesIt } from "./rules.it";
import { rulesNl } from "./rules.nl";
import { rulesPt } from "./rules.pt";

export type { MessageId };
export type ConsoleLanguage = GetStaffMeResponse["settings"]["consoleLanguage"];
export type Params = Record<string, string | number>;
/** One language's messages: every key of fr.ts, so a missing translation is a compile error. */
export type Catalog = Record<MessageId, string>;

/**
 * Every language a staff member can pick (`consoleLanguage`). Each one is
 * complete: there is no fallback to French.
 */
export const CATALOGS: Record<ConsoleLanguage, Catalog> = { fr, en, de, nl, pt, es, it, ar };
export const FEATURE_NAMES: Record<ConsoleLanguage, FeatureNames> = {
  fr: featuresFr,
  en: featuresEn,
  de: featuresDe,
  nl: featuresNl,
  pt: featuresPt,
  es: featuresEs,
  it: featuresIt,
  ar: featuresAr,
};
export const RULE_NAMES: Record<ConsoleLanguage, RuleNames> = {
  fr: rulesFr,
  en: rulesEn,
  de: rulesDe,
  nl: rulesNl,
  pt: rulesPt,
  es: rulesEs,
  it: rulesIt,
  ar: rulesAr,
};

/** The switcher's order: French first (the default), then as the contract lists them. */
export const CONSOLE_LANGUAGES = Object.keys(CATALOGS) as ConsoleLanguage[];

/** Each language in its own name, as the switcher shows it. */
export const LANGUAGE_NAMES: Record<ConsoleLanguage, string> = {
  fr: "Français",
  en: "English",
  de: "Deutsch",
  nl: "Nederlands",
  pt: "Português",
  es: "Español",
  it: "Italiano",
  ar: "العربية",
};

/**
 * The `Intl` locale for each language. Arabic keeps Latin digits, like the
 * phone numbers, IDs and amounts around it.
 */
const LOCALES: Record<ConsoleLanguage, string> = {
  fr: "fr-FR",
  en: "en-GB",
  de: "de-DE",
  nl: "nl-NL",
  pt: "pt-PT",
  es: "es-ES",
  it: "it-IT",
  ar: "ar-u-nu-latn",
};

export const isConsoleLanguage = (value: unknown): value is ConsoleLanguage =>
  typeof value === "string" && Object.hasOwn(CATALOGS, value);

export const directionOf = (language: ConsoleLanguage): "rtl" | "ltr" => (language === "ar" ? "rtl" : "ltr");

let language: ConsoleLanguage = "fr";
const listeners = new Set<() => void>();

export const getLanguage = (): ConsoleLanguage => language;
export const localeOf = (of: ConsoleLanguage = language): string => LOCALES[of];
export const isRtl = (): boolean => directionOf(language) === "rtl";

/** `lang` and `dir` on the document root: Arabic lays the whole console out right to left. */
export function applyDocumentLanguage(root: { lang: string; dir: string }, next: ConsoleLanguage): void {
  root.lang = next;
  root.dir = directionOf(next);
}

/** Follows the staff member's `consoleLanguage` setting, or the one picked for this session. */
export function setLanguage(next: ConsoleLanguage): void {
  const changed = next !== language;
  language = next;
  if (typeof document !== "undefined") applyDocumentLanguage(document.documentElement, next);
  if (changed) for (const listener of listeners) listener();
}

/** Called when the language changes; returns the unsubscribe. */
export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function t(id: MessageId, params?: Params): string {
  const template = CATALOGS[language][id];
  if (template === undefined) {
    // Unreachable with typed IDs; a computed ID outside the catalog shows as itself, loudly.
    console.error(`[i18n] No "${language}" message for "${id}"`);
    return id;
  }
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? isolate(String(params[name])) : match,
  );
}

/**
 * A message that counts something, in the form its language uses for that many.
 *
 * "1 jours" and "1 hours" are wrong in every language this console speaks, and
 * `t` alone cannot know: the count is just another parameter to it. So a key
 * that counts carries a second one — `<id>.one` — and this picks between them
 * through `Intl.PluralRules`, which knows that French says "1 jour" and Arabic
 * has six forms rather than two.
 *
 * A key with no `.one` beside it falls back to the plural form, so adding the
 * variant is what turns the behaviour on. Nothing silently reads as a missing
 * translation.
 */
export function tCount(id: MessageId, count: number, params?: Params): string {
  const category = new Intl.PluralRules(localeOf(language)).select(count);
  const variant = `${id}.${category}` as MessageId;
  const chosen = CATALOGS[language][variant] === undefined ? id : variant;
  return t(chosen, params);
}

const FSI = "⁨";
const PDI = "⁩";

/**
 * Wraps a value put into a sentence (a name, a role, a number) in Unicode
 * isolates when the console reads right to left, so "Ops Manager + Support"
 * or "10:54" keep their own order inside Arabic text. Text that already holds
 * isolated values (a "{before} ← {after}" built by `t`) is left as is: its
 * arrow must follow the sentence's direction.
 */
export function isolate(text: string): string {
  return isRtl() && !text.includes(FSI) ? `${FSI}${text}${PDI}` : text;
}

/**
 * Anything named in the catalogs falls back to its English name, never to its
 * id: a translation this build is missing still reads as words (#185). The id
 * itself is the last resort, for something newer than this build.
 */
const inEnglish = <T extends string>(
  pick: (names: FeatureNames) => Partial<Record<T, string>> | undefined,
  id: T,
): string => pick(FEATURE_NAMES[language])?.[id] ?? pick(FEATURE_NAMES.en)?.[id] ?? id;

/** A staff feature's name in the current language. */
export const featureName = (settingId: StaffFeatureId): string => inEnglish((names) => names.features, settingId);

/** What an action is called, for the audit log and "who can do this" (#185). */
export const actionName = (actionId: string): string => inEnglish((names) => names.actions, actionId as ActionId);

/** What a setting someone chose for themselves is called, on a customer's page (#185). */
export const settingName = (settingId: string): string =>
  inEnglish((names) => names.settings, settingId as OwnSettingId);

/**
 * A permission's name (ADR-0019) in the current language; one newer than this
 * build shows its ID. The English names are the catalog's own `name`.
 */
export const permissionName = (settingId: string): string =>
  inEnglish((names) => names.permissions, settingId as DefinedPermissionId);

/** A settings category's name (the grid's section titles) in the current language. */
export const categoryName = (category: SettingCategory): string => FEATURE_NAMES[language].categories[category];

/** A customer feature's name (Fonctionnalités); one newer than this build shows its ID. */
export const customerFeatureName = (settingId: UserFeatureId): string =>
  inEnglish((names) => names.customerFeatures, settingId);

/** A company rule's name (Règles); one newer than this build shows its ID. */
export const ruleName = (ruleId: DefinedRuleId): string =>
  RULE_NAMES[language].rules[ruleId] ?? RULE_NAMES.en.rules[ruleId] ?? ruleId;

/** A rule category's name. */
export const ruleCategoryName = (category: RuleCategory): string => RULE_NAMES[language].categories[category];
